import React, { useState, useEffect, useCallback, useRef } from 'react';
import './EnhancedLoginModal.css';

const GOOGLE_CLIENT_ID = '172662423555-nffff5prjf0i75ujd3gp882dg7qvlh6q.apps.googleusercontent.com';

// Use environment variable for API URL, fallback to localhost for development
const EMAIL_API_URL = process.env.REACT_APP_EMAIL_API_URL || 'http://localhost:5000';

// LocalStorage keys for pending verifications
const PENDING_VERIFICATIONS_KEY = 'pending_verifications';
const FAILED_EMAILS_KEY = 'failed_emails';

// FedCM Status tracking
const FEDCM_STATUS_KEY = 'fedcm_status';
const FEDCM_LAST_CHECK_KEY = 'fedcm_last_check';
const FEDCM_CHECK_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Track FedCM connection failures
let fedcmConnectionFailed = false;

// Check if FedCM is available in the browser
const checkFedCMStatus = () => {
  const now = Date.now();
  const lastCheck = parseInt(localStorage.getItem(FEDCM_LAST_CHECK_KEY) || '0');
  
  // Only check if enough time has passed
  if (now - lastCheck < FEDCM_CHECK_INTERVAL) {
    const cachedStatus = localStorage.getItem(FEDCM_STATUS_KEY);
    if (cachedStatus) {
      return cachedStatus === 'available';
    }
  }
  
  // Perform actual check
  const isAvailable = !!(
    window.PublicKeyCredential &&
    navigator.credentials &&
    navigator.credentials.preventSilentAccess
  );
  
  // Also check if Google API supports FedCM
  const googleFedCMSupported = !!(
    window.google?.accounts?.id &&
    typeof window.google.accounts.id.initialize === 'function'
  );
  
  const status = (isAvailable || googleFedCMSupported) && !fedcmConnectionFailed ? 'available' : 'unavailable';
  
  // Cache the result
  localStorage.setItem(FEDCM_STATUS_KEY, status);
  localStorage.setItem(FEDCM_LAST_CHECK_KEY, now.toString());
  
  return status === 'available';
};


// Google Script Loading Function with improved timeout and fallback
const loadGoogleScript = () => {
  return new Promise((resolve, reject) => {
    // Check if script already exists (check both id and oauth2 for full support)
    if (window.google?.accounts?.id && window.google?.accounts?.oauth2) {
      console.log('✅ Google script already loaded');
      resolve();
      return;
    }

    // Check if script is already being loaded
    const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      console.log('⏳ Google script already being loaded, waiting...');
      // Wait for the existing script to load
      const checkLoaded = setInterval(() => {
        if (window.google?.accounts?.id && window.google?.accounts?.oauth2) {
          clearInterval(checkLoaded);
          console.log('✅ Google script loaded from existing script');
          resolve();
        }
      }, 100);
      
      // Timeout after 15 seconds (increased for slower connections)
      setTimeout(() => {
        clearInterval(checkLoaded);
        // Check one more time before rejecting
        if (window.google?.accounts?.id && window.google?.accounts?.oauth2) {
          resolve();
        } else {
          reject(new Error('Google script loading timeout'));
        }
      }, 15000);
      return;
    }

    console.log('📜 Loading Google script...');
    
    // Create and append script
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      console.log('✅ Google script loaded successfully');
      // Give a small delay to ensure Google API is fully initialized
      setTimeout(() => {
        if (window.google?.accounts?.id && window.google?.accounts?.oauth2) {
          resolve();
        } else if (window.google?.accounts?.id) {
          // Fallback: oauth2 might not be available but id is, which is enough for basic usage
          console.log('⚠️ Google oauth2 not available but id API is present, continuing...');
          resolve();
        } else {
          reject(new Error('Google API not initialized after script load'));
        }
      }, 500);
    };
    
    script.onerror = (error) => {
      console.error('❌ Failed to load Google script:', error);
      reject(new Error('Failed to load Google OAuth script'));
    };

    document.head.appendChild(script);
  });
};

// Constants
const PASSWORD_STRENGTH_LABELS = {
  0: 'Too short',
  1: 'Weak',
  2: 'Fair',
  3: 'Good',
  4: 'Strong',
  5: 'Strong'
};

// Utility Functions
const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
const validatePassword = (password) => password.length >= 8;
const validatePhone = (phone) => !phone || /^[\+]?[1-9][\d]{0,15}$/.test(phone.replace(/\s/g, ''));

const checkPasswordStrength = (password) => {
  let strength = 0;
  if (password.length >= 8) strength += 1;
  if (/[a-z]/.test(password)) strength += 1;
  if (/[A-Z]/.test(password)) strength += 1;
  if (/[0-9]/.test(password)) strength += 1;
  if (/[^A-Za-z0-9]/.test(password)) strength += 1;
  return strength;
};

// Helper functions for localStorage fallback
const savePendingVerification = (userData) => {
  try {
    const pending = JSON.parse(localStorage.getItem(PENDING_VERIFICATIONS_KEY) || '[]');
    // Remove any existing pending verification for this email
    const filtered = pending.filter(p => p.email !== userData.email);
    filtered.push({
      ...userData,
      savedAt: new Date().toISOString(),
      retryCount: 0
    });
    localStorage.setItem(PENDING_VERIFICATIONS_KEY, JSON.stringify(filtered));
    console.log('✅ Pending verification saved locally:', userData.email);
    return true;
  } catch (error) {
    console.error('❌ Failed to save pending verification:', error);
    return false;
  }
};

const getPendingVerifications = () => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_VERIFICATIONS_KEY) || '[]');
  } catch {
    return [];
  }
};

const removePendingVerification = (email) => {
  try {
    const pending = getPendingVerifications();
    const filtered = pending.filter(p => p.email !== email);
    localStorage.setItem(PENDING_VERIFICATIONS_KEY, JSON.stringify(filtered));
  } catch (error) {
    console.error('❌ Failed to remove pending verification:', error);
  }
};

// Save failed email attempt
const saveFailedEmail = (userData, error) => {
  try {
    const failed = JSON.parse(localStorage.getItem(FAILED_EMAILS_KEY) || '[]');
    failed.push({
      email: userData.email,
      userData,
      error: error.message,
      failedAt: new Date().toISOString()
    });
    // Keep only last 10 failed emails
    if (failed.length > 10) {
      failed.shift();
    }
    localStorage.setItem(FAILED_EMAILS_KEY, JSON.stringify(failed));
  } catch (e) {
    console.error('❌ Failed to save failed email:', e);
  }
};

// Retry sending verification email
const retryPendingVerification = async (userData, retryCount = 0) => {
  const maxRetries = 3;
  const delay = Math.pow(2, retryCount) * 1000; // Exponential backoff: 1s, 2s, 4s

  console.log(`🔄 Retrying email verification for ${userData.email} (attempt ${retryCount + 1}/${maxRetries})`);

  if (retryCount < maxRetries) {
    await new Promise(resolve => setTimeout(resolve, delay));
  }

  try {
    const response = await fetch(`${EMAIL_API_URL}/api/email/registration-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        verificationToken: userData.verificationToken,
        registrationTime: userData.registrationTime
      })
    });

    const result = await response.json();

    if (result.status === 'success') {
      console.log('✅ Email verification retry successful:', userData.email);
      removePendingVerification(userData.email);
      return { success: true, messageId: result.messageId, recovered: true };
    } else {
      throw new Error(result.error || 'Email send failed');
    }
  } catch (error) {
    console.error('❌ Email verification retry failed:', error);

    if (retryCount < maxRetries - 1) {
      return retryPendingVerification(userData, retryCount + 1);
    } else {
      // All retries exhausted, save as failed
      saveFailedEmail(userData, error);
      return { success: false, error: error.message, recovered: false };
    }
  }
};

// Main email sending function with local fallback
const sendEmailVerification = async (userData, options = {}) => {
  const { useLocalFallback = false, showStatus = true } = options;

  if (showStatus) {
    console.log('📧 Sending verification email to:', userData.email);
  }

  try {
    const response = await fetch(`${EMAIL_API_URL}/api/email/registration-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        verificationToken: userData.verificationToken,
        registrationTime: userData.registrationTime
      })
    });

    const result = await response.json();

    if (result.status === 'success') {
      if (showStatus) {
        console.log('✅ Email sent successfully:', result.messageId);
      }
      return {
        success: true,
        messageId: result.messageId,
        error: null,
        localFallback: false
      };
    } else {
      throw new Error(result.error || 'Failed to send email');
    }
  } catch (error) {
    console.error('❌ Email sending failed:', error.message);

    // Try local fallback if enabled or if it's a network error
    if (useLocalFallback || error.message.includes('Failed to fetch') || error.message.includes('network')) {
      if (showStatus) {
        console.log('💾 Saving to local storage for later retry...');
      }
      const saved = savePendingVerification(userData);

      if (saved) {
        return {
          success: false,
          error: error.message,
          localFallback: true,
          message: 'Email saved locally. Will retry when connection is restored.'
        };
      }
    }

    saveFailedEmail(userData, error);
    return {
      success: false,
      error: error.message,
      localFallback: false
    };
  }
};

// Check and retry pending verifications on page load
const checkPendingVerifications = async () => {
  const pending = getPendingVerifications();

  if (pending.length === 0) {
    console.log('✅ No pending verifications to retry');
    return { attempted: 0, successful: 0, failed: 0 };
  }

  console.log(`🔄 Found ${pending.length} pending verifications to retry`);

  let successful = 0;
  let failed = 0;

  for (const verification of pending) {
    try {
      const result = await retryPendingVerification(verification, 0);
      if (result.success && result.recovered) {
        successful++;
      } else {
        failed++;
      }
    } catch (error) {
      console.error('❌ Failed to retry verification for:', verification.email);
      failed++;
    }
  }

  console.log(`✅ Pending verification recovery complete: ${successful} successful, ${failed} failed`);

  return { attempted: pending.length, successful, failed };
};

// Export helper functions for use in other components
export { 
  checkPendingVerifications, 
  getPendingVerifications, 
  removePendingVerification,
  retryPendingVerification 
};

// Form Input Component
const FormInput = ({ 
  type, 
  name, 
  value, 
  onChange, 
  placeholder, 
  error, 
  required, 
  label,
  className = '',
  onFocus,
  ...props 
}) => (
  <div className="kingsolarman-form-group">
    {label && (
      <label>
        {label}
        {required && <span className="required-star">*</span>}
      </label>
    )}
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      onFocus={onFocus}
      placeholder={placeholder}
      required={required}
      className={`kingsolarman-input ${error ? 'kingsolarman-error' : ''}`}
      {...props}
    />
    {error && <span className="kingsolarman-error-message">{error}</span>}
  </div>
);

// Password Strength Indicator Component
const PasswordStrengthIndicator = ({ password }) => {
  const strength = checkPasswordStrength(password);
  
  return (
    <div className="kingsolarman-password-strength">
      <div className={`kingsolarman-strength-bar strength-${strength}`}>
        <div className="kingsolarman-strength-fill"></div>
      </div>
      <span className="kingsolarman-strength-text">
        Password strength: {PASSWORD_STRENGTH_LABELS[strength]}
      </span>
    </div>
  );
};

// Email Verification Component
const EmailVerificationView = ({ email, onClose }) => (
  <div className="kingsolarman-email-verification">
    <div className="kingsolarman-email-verification-icon">📧</div>
    <h3>Check Your Email!</h3>
    <p>We've sent a verification link to:</p>
    <p className="kingsolarman-email-address">{email}</p>
    <p>
      Please click the link in the email to verify your account. 
      You can close this modal and we'll automatically log you in once you verify your email.
    </p>
    <button 
      className="kingsolarman-verification-close-btn"
      onClick={onClose}
    >
      Got it!
    </button>
  </div>
);

// Social Login Button Component
const SocialLoginButton = ({ 
  provider, 
  isLoading, 
  onClick, 
  icon
}) => (
  <button 
    className={`kingsolarman-social-btn ${provider.toLowerCase()}-btn`}
    onClick={onClick}
    disabled={isLoading}
    type="button"
  >
    {icon}
    {isLoading ? 'Signing in...' : `Continue with ${provider}`}
  </button>
);

// Main Component
const EnhancedLoginModal = ({ 
  isOpen, 
  onClose, 
  title = "Welcome to KingSolarman",
  subtitle = "Join us to unlock these amazing benefits:",
  benefits = [
    { icon: "🛒", text: "Save your cart across devices" },
    { icon: "❤️", text: "Keep your wishlist forever" },
    { icon: "📋", text: "Track your order history" }
  ],
  isMandatory = false,
  contextMessage = ""
}) => {
  const [authMode, setAuthMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    phone: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [isGoogleReady, setIsGoogleReady] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  // Default to legacy mode since FedCM has connection issues with Google's servers
  const [fedcmAvailable, setFedcmAvailable] = useState(false);
  const [showLegacyFallback, setShowLegacyFallback] = useState(true);
  const googleRetryCount = useRef(0);
  const maxRetries = 3;

  const [showEmailVerification, setShowEmailVerification] = useState(false);

  // Check for pending verifications when modal opens
  useEffect(() => {
    if (isOpen) {
      console.log('🔄 Modal opened, checking for pending verifications...');
      
      // Try to recover any pending verifications
      checkPendingVerifications().then(result => {
        if (result.attempted > 0) {
          console.log(`📧 Verification recovery: ${result.successful}/${result.attempted} successful`);
          if (result.successful > 0) {
            alert(`Great news! ${result.successful} pending verification email(s) have been sent.`);
          }
        }
      }).catch(error => {
        console.error('❌ Error checking pending verifications:', error);
      });
    }
  }, [isOpen]);

  // Load Google script on component mount
  useEffect(() => {
    console.log('🔄 Setting up Google Sign-in...');
    
    const initGoogle = async () => {
      try {
        await loadGoogleScript();
        setGoogleError(null);
        // Initialize if modal is open
        if (isOpen) {
          initializeGoogleSignIn();
        }
      } catch (error) {
        console.error('❌ Failed to load Google script:', error);
        setGoogleError(error.message);
        setIsGoogleReady(false);
      }
    };

    initGoogle();

    return () => {
      console.log('🧹 Cleaning up Google Sign-in setup');
    };
  }, []);

  // Initialize Google Sign-in when modal opens
  useEffect(() => {
    if (isOpen && window.google?.accounts?.id) {
      initializeGoogleSignIn();
    }
  }, [isOpen]);

  const initializeGoogleSignIn = useCallback(() => {
    if (!window.google?.accounts?.id) {
      console.log('⚠️ Google accounts API not available yet');
      return;
    }
    
    try {
      // Initialize with FedCM support
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleResponse,
        auto_select: false,
        cancel_on_tap_outside: false,
        // FedCM-specific options
        itp_support: true,
        // Enable login hint for better UX
        login_hint: '',
        // Enable native handler for better FedCM support
        use_fedcm_for_prompt: true
      });
      
      // Try to render the button in the hidden div
      try {
        window.google.accounts.id.renderButton(
          document.getElementById('google-signin-button'),
          {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            logo_alignment: 'left',
            width: 250
          }
        );
      } catch (renderError) {
        console.warn('⚠️ Button render warning (non-critical):', renderError.message);
      }
      
      // Enable one-tap if available
      try {
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            console.log('🔔 Google One-Tap not displayed:', notification.getNotDisplayedReason());
            // Check for FedCM-specific issues
            if (notification.getNotDisplayedReason() === 'opt_out_or_error') {
              setFedcmAvailable(false);
            }
          }
          if (notification.isSkippedMoment()) {
            console.log('🔔 Google One-Tap skipped:', notification.getSkippedReason());
          }
          if (notification.isDismissedMoment()) {
            console.log('🔔 Google One-Tap dismissed:', notification.getDismissedReason());
          }
        });
      } catch (promptError) {
        console.warn('⚠️ One-Tap prompt warning (non-critical):', promptError.message);
      }
      
      setIsGoogleReady(true);
      setGoogleError(null);
      console.log('✅ Google Sign-in initialized successfully');
    } catch (error) {
      console.error('❌ Failed to initialize Google Sign-in:', error);
      setGoogleError(error.message);
      setIsGoogleReady(false);
    }
  }, []);

  const handleGoogleResponse = async (response) => {
    setIsGoogleLoading(true);
    
    try {
      const base64Url = response.credential.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const userInfo = JSON.parse(jsonPayload);
      const userData = {
        id: userInfo.sub,
        email: userInfo.email,
        name: userInfo.name,
        given_name: userInfo.given_name,
        family_name: userInfo.family_name,
        picture: userInfo.picture,
        provider: 'google',
        token: response.credential,
        verified_email: userInfo.email_verified
      };

      localStorage.setItem('user', JSON.stringify(userData));
      alert('Successfully signed in with Google! Welcome to KingSolarMan!');
      onClose();
      window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: userData }));
      
    } catch (error) {
      console.error('Google sign-in error:', error);
      alert('Google sign-in failed. Please try again.');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleSignIn = useCallback(async () => {
    // Since FedCM has connection issues with Google's servers, use legacy OAuth2 by default
    await handleLegacyGoogleSignIn();
  }, []);


  // Legacy popup-based Google Sign-In fallback for when FedCM fails
  const handleLegacyGoogleSignIn = async () => {
    try {
      setIsGoogleLoading(true);
      setGoogleError(null);

      // Ensure Google script is loaded first
      if (!window.google?.accounts?.oauth2) {
        await loadGoogleScript();
      }

      // Create legacy OAuth2 client for popup-based flow
      const legacyClient = google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        callback: async (response) => {
          if (response.error) {
            console.error('❌ Legacy Google Sign-In error:', response.error);
            setGoogleError(`Sign-in failed: ${response.error}`);
            setIsGoogleLoading(false);
            return;
          }

          // Get user info from Google
          try {
            const userInfoResponse = await fetch(
              'https://www.googleapis.com/oauth2/v2/userinfo',
              {
                headers: {
                  Authorization: `Bearer ${response.access_token}`
                }
              }
            );

            const userInfo = await userInfoResponse.json();

            const userData = {
              id: userInfo.id,
              email: userInfo.email,
              name: userInfo.name,
              given_name: userInfo.given_name,
              family_name: userInfo.family_name,
              picture: userInfo.picture,
              provider: 'google',
              accessToken: response.access_token,
              verified_email: userInfo.verified_email
            };

            localStorage.setItem('user', JSON.stringify(userData));
            alert('Successfully signed in with Google! Welcome to KingSolarMan!');
            onClose();
            window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: userData }));
          } catch (error) {
            console.error('❌ Failed to get user info:', error);
            setGoogleError('Failed to retrieve user information. Please try again.');
          } finally {
            setIsGoogleLoading(false);
          }
        }
      });

      legacyClient.requestAccessToken({ prompt: 'select_account' });
    } catch (error) {
      console.error('❌ Legacy Google Sign-In setup failed:', error);
      setGoogleError('Google Sign-in is currently unavailable. Please try again later or use email login.');
      setIsGoogleLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    e.stopPropagation();
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleClearError = (fieldName) => {
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters long';
    }

    // Registration specific validations
    if (authMode === 'register') {
      if (!formData.firstName?.trim()) {
        newErrors.firstName = 'First name is required';
      }
      if (!formData.lastName?.trim()) {
        newErrors.lastName = 'Last name is required';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      
      if (formData.phone && !validatePhone(formData.phone)) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!validateForm()) return;

    setIsLoading(true);
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      if (authMode === 'login') {
        const existingUser = localStorage.getItem('user');
        let userData;
        
        if (existingUser) {
          const parsedUser = JSON.parse(existingUser);
          if (parsedUser.email === formData.email && !parsedUser.verified) {
            alert('Please verify your email address before logging in.');
            setIsLoading(false);
            return;
          }
          userData = parsedUser;
        } else {
          userData = {
            id: Date.now().toString(),
            email: formData.email,
            name: `${formData.firstName || 'User'} ${formData.lastName || ''}`.trim(),
            provider: 'manual',
            verified: true
          };
        }
        
        localStorage.setItem('user', JSON.stringify(userData));
        alert('Login successful! Welcome back.');
        onClose();
        window.dispatchEvent(new CustomEvent('userLoggedIn', { detail: userData }));
        
      } else {
        const verificationToken = 'REG-' + Date.now();
        const userData = {
          id: Date.now().toString(),
          email: formData.email,
          firstName: formData.firstName,
          lastName: formData.lastName,
          name: `${formData.firstName} ${formData.lastName}`,
          provider: 'manual',
          phone: formData.phone,
          verified: false,
          verificationToken: verificationToken,
          registrationTime: new Date().toISOString()
        };
        
        // Store pending verification for local fallback
        localStorage.setItem('pendingVerification', JSON.stringify(userData));
        
        // Send email with local fallback enabled
        const emailResult = await sendEmailVerification(userData, { 
          useLocalFallback: true,
          showStatus: true 
        });
        
        if (emailResult.success) {
          console.log('✅ Email sent successfully:', emailResult.messageId);
        } else if (emailResult.localFallback) {
          console.log('💾 Email saved for later delivery:', emailResult.message);
        } else {
          console.warn('⚠️ Email sending failed:', emailResult.error);
          // Still allow registration to proceed
        }
        
        setShowEmailVerification(true);
        
        // Show info message if using local fallback
        if (emailResult.localFallback) {
          console.info('📧 Note: Email will be delivered when connection is restored.');
        }
        
        window.dispatchEvent(new CustomEvent('userRegistered', { detail: userData }));
      }
      
    } catch (error) {
      console.error('Authentication error:', error);
      alert('Authentication failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTabChange = (mode) => {
    setAuthMode(mode);
    setErrors({});
    setShowEmailVerification(false);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      phone: ''
    });
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !isMandatory) {
      onClose();
    }
  };

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isMandatory) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, isMandatory, onClose]);

  if (!isOpen) return null;

  return (
    <div className="kingsolarman-enhanced-overlay" onClick={handleOverlayClick}>
      <div className="kingsolarman-enhanced-modal" onClick={(e) => e.stopPropagation()}>
        <div className="kingsolarman-modal-header">
          <h2 className="kingsolarman-modal-title">{title}</h2>
          {!isMandatory && (
            <button 
              className="kingsolarman-close-btn" 
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              aria-label="Close modal"
            >
              ×
            </button>
          )}
        </div>

        {contextMessage && (
          <div className="kingsolarman-context-message">
            <p>{contextMessage}</p>
          </div>
        )}

        <div className="kingsolarman-modal-body">
          {showEmailVerification ? (
            <EmailVerificationView 
              email={formData.email} 
              onClose={(e) => {
                e?.stopPropagation();
                onClose();
              }}
            />
          ) : (
            <>
              <div className="kingsolarman-auth-section">
                <h3 className="kingsolarman-auth-title">
                  {authMode === 'login' ? 'Welcome Back!' : 'Create Your Account'}
                </h3>
                
                <div className="kingsolarman-tab-container">
                  <div className="kingsolarman-tabs">
                    <button
                      className={`kingsolarman-tab ${authMode === 'login' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTabChange('login');
                      }}
                    >
                      Log In
                    </button>
                    <button
                      className={`kingsolarman-tab ${authMode === 'register' ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTabChange('register');
                      }}
                    >
                      Sign Up
                    </button>
                  </div>
                </div>

                <div className="kingsolarman-social-login">

                  

                  <div className="kingsolarman-social-buttons">
                    
                    <SocialLoginButton
                      provider="Google"
                      isLoading={isGoogleLoading}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLegacyGoogleSignIn();
                      }}
                      icon={
                        <svg className="kingsolarman-social-icon google-icon" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                      }
                    />
                    

                  </div>

                  <div className="kingsolarman-email-divider">
                    <span>or use email</span>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="kingsolarman-form">
                  {authMode === 'register' && (
                    <div className="kingsolarman-form-row">
                      <FormInput
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        onFocus={() => handleClearError('firstName')}
                        placeholder="Enter your first name"
                        error={errors.firstName}
                        required
                        label="First Name"
                      />
                      <FormInput
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        onFocus={() => handleClearError('lastName')}
                        placeholder="Enter your last name"
                        error={errors.lastName}
                        required
                        label="Last Name"
                      />
                    </div>
                  )}

                  <FormInput
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onFocus={() => handleClearError('email')}
                    placeholder="Enter your email"
                    error={errors.email}
                    required
                    label="Email Address"
                  />

                  {authMode === 'register' && (
                    <FormInput
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      onFocus={() => handleClearError('phone')}
                      placeholder="Enter your phone number"
                      error={errors.phone}
                      label="Phone Number (Optional)"
                    />
                  )}

                  <div className="kingsolarman-form-group">
                    <FormInput
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      onFocus={() => handleClearError('password')}
                      placeholder="Enter your password"
                      error={errors.password}
                      required
                      label="Password"
                    />
                    {authMode === 'register' && formData.password && (
                      <PasswordStrengthIndicator password={formData.password} />
                    )}
                  </div>

                  {authMode === 'register' && (
                    <FormInput
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      onFocus={() => handleClearError('confirmPassword')}
                      placeholder="Confirm your password"
                      error={errors.confirmPassword}
                      required
                      label="Confirm Password"
                    />
                  )}

                  <button 
                    type="submit" 
                    className="kingsolarman-submit-btn"
                    disabled={isLoading || isGoogleLoading}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isLoading 
                      ? 'Processing...' 
                      : authMode === 'login' 
                        ? 'Log In' 
                        : 'Create Account'
                    }
                  </button>
                </form>

                <div className="kingsolarman-terms">
                  <p>
                    By continuing, you agree to our{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                      Privacy Policy
                    </a>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <div id="google-signin-button" style={{ display: 'none' }}></div>
    </div>
  );
};

export default EnhancedLoginModal;